<!-- eslint-disable vue/no-v-html -->
<template>
  <section class="eiendomsskatt">
    <system-notification komponent-id="eiendomsskatt" :allow-partial-hiding-driftsmelding="true"></system-notification>

    <h1 class="ok-title-1">
      {{
        representatingOrg
          ? $t('skattOgAvgift.eiendomsskatt.pageTitle.org')
          : $t('skattOgAvgift.eiendomsskatt.pageTitle.person')
      }}
    </h1>

    <span class="ods-text--weight-light"
      ><p v-html="$sanitize($t('skattOgAvgift.eiendomsskatt.ingress', placeholders))"></p>
    </span>

    <span class="ods-text--weight-light"
      ><p v-html="$sanitize($t('skattOgAvgift.eiendomsskatt.links.lesMer', placeholders))"></p
    ></span>

    <section class="ok-section ok-section--margin-top">
      <h2 class="ok-title-2">{{ $t('skattOgAvgift.eiendomsskatt.subTitle') }}</h2>
      <div v-if="skatteeiendommerLoading">
        <ods-spinner />
      </div>
      <div v-else-if="skatteeiendommerError" class="ok-alert ok-alert--danger" role="alert">
        {{ $t('skattOgAvgift.eiendomsskatt.states.failed') }}
      </div>

      <ul v-if="skatteeiendommer" class="_">
        <li v-for="(skatteeiendom, index) in skatteeiendommer" :key="index" class="ok-section--frame">
          <h3 v-if="skatteeiendom.matrikkelEnhet" class="ok-title-4">
            {{ matrikkeladresseToString(skatteeiendom.matrikkelEnhet.matrikkelAdresse) }}
            <small
              class="title-description ods-text--weight-light"
              v-html="$sanitize($t('skattOgAvgift.eiendomsskatt.skatteeiendommer.gbnrLang'))"
            ></small>
          </h3>
          <p>{{ skatteeiendom.matrikkelEnhet.adresse }}</p>
          <div class="ok-row ods-text--weight-light">
            <div class="ok-col-9 eiendomsskatt ods-text--weight-light">
              <div class="flex">
                <h4>
                  <strong v-if="kanViseBeregning(skatteeiendom)">{{
                    $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.eiendomsskattAr', {
                      skattear:
                        skatteeiendom.utskrivningsvedtak.length > 0
                          ? skatteeiendom.utskrivningsvedtak[0].aktueltAar
                          : '',
                    })
                  }}</strong>
                </h4>
                <div v-if="kanViseBeregning(skatteeiendom)" class="flex--grow">
                  <strong v-html="eiendomsskattValue(skatteeiendom.utskrivningsvedtak[0])"></strong>
                  <button
                    type="button"
                    class="ok-btn ok-btn--small pull-right"
                    @click="skatteeiendom.visBeregning = !skatteeiendom.visBeregning"
                  >
                    {{
                      $t(
                        `skattOgAvgift.eiendomsskatt.skatteeiendommer.taksttype.${
                          skatteeiendom.visBeregning ? 'skjulBeregning' : 'visBeregning'
                        }`
                      )
                    }}
                  </button>
                </div>
              </div>
              <div
                v-for="(utskrivningsvedtak, utskrvingsvedtakIndex) in skatteeiendom.utskrivningsvedtak"
                :key="utskrvingsvedtakIndex"
              >
                <span v-if="skatteeiendom.matrikkelEnhet.harBortfesterAvtale">
                  Eiendomsskatt {{ new Date().getFullYear() }}:
                  <br />
                  {{ $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.bortfester') }}
                </span>
                <span v-if="utskrivningsvedtak.skatteverdi !== 0 && !utskrivningsvedtak.skatteverdi">{{
                  $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.underBehandling')
                }}</span>
                <div class="ok-button-row">
                  <a
                    v-if="skatteeiendom.matrikkelEnhet.harBortfesterAvtale"
                    :href="klageUrl(skatteeiendom).href"
                    class="ok-btn ok-btn--ghost"
                  >
                    {{ klageUrl(skatteeiendom).text }}
                  </a>
                </div>
                <div v-if="kanViseBeregning(skatteeiendom)">
                  <aside v-show="skatteeiendom.visBeregning" class="ok-section">
                    <div class="ok-table-container">
                      <table
                        v-show="skatteeiendom.visBeregning"
                        :fadeVisible="skatteeiendom.visBeregning"
                        class="ok-table"
                      >
                        <thead>
                          <tr>
                            <th class="ok-table__th"></th>
                            <th
                              v-for="(skatteverditype, takstIndex) in utskrivningsvedtak.skatteverdityper"
                              :key="`taksktype-${takstIndex}`"
                              class="ok-table__th text-right"
                            >
                              {{ takstTypeDisplayNavn(skatteverditype.skatteverditype) }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-if="harTaksttypeFormuesgrunnlag(utskrivningsvedtak)">
                            <td>
                              {{ $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.taksttype.beregnetMarkedsverdi') }}
                            </td>
                            <td
                              v-for="(skatteverditype, boligIndex) in utskrivningsvedtak.skatteverdityper"
                              :key="`taksktype-beregnetMarkedsverdi-${boligIndex}`"
                              class="text-right"
                            >
                              {{
                                skatteverditype.skatteverditype === 'F'
                                  ? formatNumber(skatteverditype.enhetverdi, true)
                                  : ''
                              }}
                            </td>
                          </tr>
                          <tr v-if="harTaksttypeFormuesgrunnlag(utskrivningsvedtak)">
                            <td>
                              {{ $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.taksttype.beregnetMarkedsverdi70') }}
                            </td>
                            <td
                              v-for="(skatteverditype, bolig70Index) in utskrivningsvedtak.skatteverdityper"
                              :key="`taksktype-beregnetMarkedsverdi70-${bolig70Index}`"
                              class="text-right"
                            >
                              {{
                                skatteverditype.skatteverditype === 'F'
                                  ? formatNumber(skatteverditype.eiendomsskatteGrunnlag)
                                  : ''
                              }}
                            </td>
                          </tr>
                          <tr v-if="!harKunTaksttypeFormuesgrunnlagEllerSerskiltgrunnlag(utskrivningsvedtak)">
                            <td>
                              {{ $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.taksttype.eiendomsskattetakst') }}
                            </td>
                            <td
                              v-for="(skatteverditype, skattetakstIndex) in utskrivningsvedtak.skatteverdityper"
                              :key="`taksktype-eiendomsskattetakst-${skattetakstIndex}`"
                              class="text-right"
                            >
                              {{
                                skatteverditype.skatteverditype === 'F' || skatteverditype.skatteverditype === 'V'
                                  ? ''
                                  : formatNumber(skatteverditype.enhetverdi)
                              }}
                            </td>
                          </tr>
                          <tr
                            v-if="
                              skalSkatteverditypeVises(utskrivningsvedtak, ['F']) &&
                              harTaksttypeBolig(utskrivningsvedtak)
                            "
                          >
                            <td>
                              {{ $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.taksttype.takstreduksjon') }}
                            </td>
                            <td
                              v-for="(skatteverditype, takstreduksjonIndex) in utskrivningsvedtak.skatteverdityper"
                              :key="`taksktype-takstreduksjon-${takstreduksjonIndex}`"
                              class="text-right"
                            >
                              {{
                                skatteverditype.skatteverditype === 'B'
                                  ? formatNumber(skatteverditype.eiendomsskatteGrunnlag)
                                  : ''
                              }}
                            </td>
                          </tr>
                          <tr v-if="harTaksttypeSaerskiltGrunnlag(utskrivningsvedtak)">
                            <td>
                              {{ $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.taksttype.saerskiltGrunnlag') }}
                            </td>
                            <td
                              v-for="(skatteverditype, saerskiltIndex) in utskrivningsvedtak.skatteverdityper"
                              :key="`taksktype-saerskiltGrunnlag-${saerskiltIndex}`"
                              class="text-right"
                            >
                              {{
                                skatteverditype.skatteverditype === 'V' ? formatNumber(skatteverditype.enhetverdi) : ''
                              }}
                            </td>
                          </tr>
                          <tr
                            v-if="
                              harTaksttypeBolig(utskrivningsvedtak) || harTaksttypeFormuesgrunnlag(utskrivningsvedtak)
                            "
                          >
                            <td>
                              {{ $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.taksttype.bunnfradrag') }}
                            </td>
                            <td
                              v-for="(skatteverditype, bunnfradragIndex) in utskrivningsvedtak.skatteverdityper"
                              :key="`taksktype-bunnfradrag-${bunnfradragIndex}`"
                              class="text-right"
                            >
                              {{
                                skatteverditype.skatteverditype === 'B' || skatteverditype.skatteverditype === 'F'
                                  ? formatNumber(skatteverditype.bunnfradrag)
                                  : ''
                              }}
                            </td>
                          </tr>
                          <tr v-if="harTaksttypeVerdiIkkeNull(utskrivningsvedtak, 'fritak')">
                            <td>{{ $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.taksttype.fritak') }}</td>
                            <td
                              v-for="(skatteverditype, fritakIndex) in utskrivningsvedtak.skatteverdityper"
                              :key="`taksktype-fritak-${fritakIndex}`"
                              class="text-right"
                            >
                              {{ skatteverditype.fritak !== 0.0 ? formatNumber(skatteverditype.fritak) : '' }}
                            </td>
                          </tr>
                          <tr v-if="harTaksttypeMedPropertyNotNull(utskrivningsvedtak, 'beregningsgrunnlag')">
                            <td>
                              {{ $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.taksttype.beregningsgrunnlag') }}
                            </td>
                            <td
                              v-for="(skatteverditype, beregningsIndex) in utskrivningsvedtak.skatteverdityper"
                              :key="`taksktype-beregningsgrunnlag-${beregningsIndex}`"
                              class="text-right"
                            >
                              {{ formatNumber(skatteverditype.beregningsgrunnlag) }}
                            </td>
                          </tr>
                          <tr v-if="harTaksttypeMedPropertyNotNull(utskrivningsvedtak, 'skattesats')">
                            <td>{{ $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.taksttype.skattesats') }}</td>
                            <td
                              v-for="(skatteverditype, skattesatsIndex) in utskrivningsvedtak.skatteverdityper"
                              :key="`taksktype-skattesats-${skattesatsIndex}`"
                              class="text-right"
                            >
                              {{ skatteverditype.skattesats }}&nbsp;&permil;
                            </td>
                          </tr>
                          <tr v-if="harTaksttypeMedPropertyNotNull(utskrivningsvedtak, 'skatteverdi')">
                            <td>
                              {{ $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.taksttype.eiendomsskatt') }}
                            </td>
                            <td
                              v-for="(skatteverditype, eiendomsskattIndex) in utskrivningsvedtak.skatteverdityper"
                              :key="`taksktype-eiendomsskatt-${eiendomsskattIndex}`"
                              class="text-right"
                            >
                              {{ formatNumber(skatteverditype.skatteverdi) }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div
                      v-if="harTaksttypeSaerskiltGrunnlag(utskrivningsvedtak)"
                      v-show="skatteeiendom.visBeregning"
                      class="ok-alert"
                      :fadeVisible="skatteeiendom.visBeregning"
                      v-html="
                        $sanitize(
                          $t(
                            `skattOgAvgift.eiendomsskatt.skatteeiendommer.saerskiltGrunnlagTekst${utskrivningsvedtak.aktueltAar}`
                          )
                        )
                      "
                    ></div>
                    <div
                      v-if="utskrivningsvedtak.skatteverdi !== 0 && utskrivningsvedtak.skatteverdi"
                      v-show="skatteeiendom.visBeregning"
                      :fadeVisible="skatteeiendom.visBeregning"
                    >
                      <small>
                        {{ $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.flereDetaljerBeregning') }}
                      </small>
                    </div>
                  </aside>
                  <div class="ok-button-row">
                    <a
                      v-if="utskrivningsvedtak && klageveilederTilgjengelig(utskrivningsvedtak)"
                      :href="klageUrl(skatteeiendom).href"
                      class="ok-btn ok-btn--ghost"
                    >
                      {{ klageUrl(skatteeiendom).text }}
                    </a>
                  </div>
                  <div v-if="skatteeiendom.harKlaget" class="ok-alert ok-alert--warning">
                    {{ $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.klageInnsendt') }}
                  </div>
                </div>
              </div>
              <div v-if="skatteeiendom.utskrivningsvedtak.length === 0">
                {{
                  $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.ikkeVedtatt', {
                    skattear: new Date().getFullYear(),
                  })
                }}
              </div>
            </div>
            <div
              v-for="(utskrivningsvedtak, utskrivningsvedtakIndex) in skatteeiendom.utskrivningsvedtak"
              :key="utskrivningsvedtakIndex"
              class="ok-col-3 fakturamottaker"
            >
              <div v-if="utskrivningsvedtak.status !== 'erutgått' && !skatteeiendom.matrikkelEnhet.harBortfesterAvtale">
                <div v-if="utskrivningsvedtak.fakturamottaker">
                  <h4>
                    <strong>{{ $t('skattOgAvgift.eiendomsskatt.skatteeiendommer.fakturamottaker') }}</strong>
                  </h4>
                  <p v-html="$sanitize(fakturamottaker(utskrivningsvedtak.fakturamottaker))"></p>
                </div>
              </div>
            </div>
          </div>
        </li>
      </ul>
      <div id="vue-pagination">
        <ods-pagination
          v-if="!skatteeiendommerLoading && !skatteeiendommerError && skatteeiendommer"
          class="ods-flex ods-flex-justify-content-center"
          aria-label="Pagination skatteeiendommer"
          :total-pages="pageCount"
          :current-index="current"
          @paginate="setPage"
        />
      </div>
      <div v-if="totalCount === 0" class="ok-alert ok-alert--warning" role="alert">
        {{ $sanitize($t('skattOgAvgift.eiendomsskatt.states.empty')) }}
      </div>
    </section>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import apiClient from '../api/ApiClient';
import i18n from '../locales/i18n';
import OdsSpinner from '../global-components/OdsSpinner.vue';
import OdsPagination from '../global-components/OdsPagination.vue';
import SystemNotification from '../global-components/SystemNotification.vue';

export default {
  // eslint-disable-next-line
  name: 'Eiendomsskatt',

  components: {
    OdsPagination,
    OdsSpinner,
    SystemNotification,
  },

  filters: {
    decimalToPermil(number) {
      return number * 1000;
    },
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (vm.getOrgsToRepresent.length === 0) {
        vm.setBacklink({
          text: vm.$t('breadcrumb.foreldreside.text'),
          href: vm.$t('breadcrumb.foreldreside.href'),
        });
      } else {
        vm.setBacklink({
          text: vm.$t('breadcrumb.representasjonsside.text'),
          href: '/',
        });
      }
    });
  },

  data: () => ({
    aktueltAar: new Date().getFullYear(),
    skatteperiodeId: null,
    skatteeiendommer: null,
    skatteeiendommerLoading: true,
    skatteeiendommerError: false,
    skatteeiendommerEmpty: false,
    pageNumber: 1,
    totalCount: null,
    current: 1,
    pageSize: 7,
    taksttyper: [
      { key: 'B', displayName: i18n.global.t('skattOgAvgift.eiendomsskatt.taksttyper.bolig') },
      { key: 'N', displayName: i18n.global.t('skattOgAvgift.eiendomsskatt.taksttyper.naering') },
      { key: 'V', displayName: i18n.global.t('skattOgAvgift.eiendomsskatt.taksttyper.saerskiltGrunnlag') },
      { key: 'K', displayName: i18n.global.t('skattOgAvgift.eiendomsskatt.taksttyper.kraftOgTele') },
      { key: 'U', displayName: i18n.global.t('skattOgAvgift.eiendomsskatt.taksttyper.ubebygdBoligtomt') },
      {
        key: 'T',
        displayName: i18n.global.t('skattOgAvgift.eiendomsskatt.taksttyper.ubebygdNeringstomt'),
      },
      { key: 'F', displayName: i18n.global.t('skattOgAvgift.eiendomsskatt.taksttyper.formuesgrunnlag') },
    ],
  }),

  computed: {
    ...mapState('representasjoner', ['currentRepresentation']),
    ...mapGetters('representasjoner', ['getOrgsToRepresent']),
    ...mapState(['environments']),
    representatingOrg() {
      return (
        this.currentRepresentation &&
        this.currentRepresentation.identifikator &&
        !this.currentRepresentation.erPrivatPerson
      );
    },
    pageCount() {
      return Math.ceil(this.totalCount / this.pageSize);
    },
    placeholders() {
      return {
        break: '<br>',
        strong_start: '<strong>',
        strong_stop: '</strong>',
        linklesmeromeiendomskatt:
          "<a href='https://www.oslo.kommune.no/skatt-og-naring/skatt-og-avgift/eiendomsskatt/'>Les mer om eiendomskatt i Oslo kommune</a>",
        linkokonomiogforvaltningetaten:
          "<a href='https://www.oslo.kommune.no/etater-foretak-og-ombud/okonomi-og-forvaltningsetaten/'>Økonomi- og forvaltningsetaten</a>",
        fakturaoversiktUrl: this.getFakturaoversiktUrl(),
      };
    },
  },

  watch: {
    currentRepresentation() {
      this.getSkatteperiodeId(this.aktueltAar);
    },
  },

  created() {
    if (this.currentRepresentation.identifikator) {
      this.getSkatteperiodeId(this.aktueltAar);
    }
  },

  methods: {
    ...mapActions(['setBacklink']),

    async getSkatteperiodeId(akueltAar) {
      try {
        const res = await apiClient.getSkatteperiodeId(akueltAar);
        if (res === null) {
          this.skatteeiendommerError = true;
        } else {
          this.skatteperiodeId = res;
        }
        await this.getSkatteeiendommer(this.skatteperiodeId, this.currentRepresentation.identifikator, 0);
      } catch (error) {
        this.skatteeiendommerError = true;
      }
    },

    async getSkatteeiendommer(skatteperiodeId, indentitetId, startIndex) {
      this.skatteeiendommerLoading = true;
      try {
        const response = await apiClient.getSkatteiendommer(skatteperiodeId, indentitetId);
        let count = 0;
        if (response) {
          response.forEach((skatteeiendom, index) => {
            // eslint-disable-next-line no-param-reassign
            skatteeiendom.visBeregning = index === 0;
            count += 1;
          });
          this.skatteeiendommer = response.slice(startIndex, startIndex + this.pageSize);
        }
        this.totalCount = count;
        this.skatteeiendommerLoading = false;
        this.skatteeiendommerError = false;
      } catch {
        this.skatteeiendommer = null;
        this.skatteeiendommerLoading = false;
        this.skatteeiendommerError = true;
      }
    },

    matrikkeladresseToString(matrikkelAdresse) {
      const parts = [
        matrikkelAdresse.grdsnummer,
        matrikkelAdresse.bruksnummer,
        matrikkelAdresse.festenummer || 0,
        matrikkelAdresse.seksjonsnummer || 0,
      ];
      return parts.join('/');
    },

    kanViseBeregning(skatteeiendom) {
      // Når en eiendom «harBortfesterAvtale: true,» skal eiendommen vises på Min side for både bortfester og fester,
      // og den vil komme opp i tjenesten både når innlogget bruker er bortfester (hjemmelshaver) og fester.
      // Men; når «harBortfesterAvtale: true,» skal utskrevet skatt ikke vises på Min side, det gjelder både får fester og bortfester.
      return !skatteeiendom.matrikkelEnhet.harBortfesterAvtale && skatteeiendom.utskrivningsvedtak.length > 0;
    },

    klageveilederTilgjengelig(utskrivingsvedtak) {
      const dayDate = Date.now();
      const tilgjengeligTilDato = Date.parse(utskrivingsvedtak.klageveilederTilgjengeligTilDato);
      return tilgjengeligTilDato > dayDate;
    },

    fakturamottaker(fakturamottaker) {
      const elements = [];
      elements.push(fakturamottaker.navn.trim());
      if (fakturamottaker.adresse1 === null) {
        elements.push(`<br>${fakturamottaker.adresse2.trim()}`);
      } else {
        elements.push(`<br>${fakturamottaker.adresse1.trim()}`);
      }
      const postadresse = `<br>${fakturamottaker.postnummer ? fakturamottaker.postnummer : ''} ${
        fakturamottaker.poststed ? fakturamottaker.poststed : ''
      }`;
      elements.push(postadresse.trim());
      elements.push(fakturamottaker.land);
      return elements.filter((adr) => !this.isEmpty(adr)).join(', ');
    },

    fakturamottakerUrl(skatteeiendom) {
      if (skatteeiendom.matrikkelEnhet.matrikkelAdresse) {
        const parts = [
          skatteeiendom.matrikkelEnhet.matrikkelAdresse.grdsnummer,
          skatteeiendom.matrikkelEnhet.matrikkelAdresse.bruksnummer,
          skatteeiendom.matrikkelEnhet.matrikkelAdresse.festenummer || 0,
          skatteeiendom.matrikkelEnhet.matrikkelAdresse.seksjonsnummer || 0,
        ];
        const gbfs = parts.join('.');
        const skattear =
          skatteeiendom.utskrivningsvedtak.length > 0 ? skatteeiendom.utskrivningsvedtak[0].aktueltAar : '';
        // TODO add baseUrl:
        /* eslint-disable-next-line max-len */
        return `/ekstern/veiledere/eiendomsskatt/fakturamottaker-eier/#?gbfs=${gbfs}&delnr=${1}&skattear=${skattear}`;
      }
      return '';
    },

    klageUrl(skatteeiendom) {
      if (skatteeiendom.matrikkelEnhet.matrikkelAdresse) {
        const representation = this.representatingOrg
          ? `orgnr=${this.currentRepresentation.identifikator}`
          : `privat=1`;

        const url = `${this.environments.VUE_APP_DVD_KLAGE_703}?${representation}&skatteiendomid=${skatteeiendom.matrikkelEnhet.skatteeiendomId}`;

        return {
          href: url,
          text: this.$t('skattOgAvgift.eiendomsskatt.links.klage'),
        };
      }
      return {};
    },

    getFakturaoversiktUrl() {
      return `<a href="${this.environments.VUE_APP_FAKTURAOVERSIKT_URL}">fakturaoversikten</a>`;
    },

    eiendomsskattValue(utskrivingsvedtak) {
      return this.formatToNok(utskrivingsvedtak.skatteverdi);
    },

    currencyFormat(val, nDecimalDigits, decimalSeparator, thousandsSeparator) {
      val = this.toNumber(val); //eslint-disable-line
      nDecimalDigits = nDecimalDigits == null ? 2 : nDecimalDigits; //eslint-disable-line
      decimalSeparator = decimalSeparator == null ? '.' : decimalSeparator; //eslint-disable-line
      thousandsSeparator = thousandsSeparator == null ? ',' : thousandsSeparator; //eslint-disable-line

      // can't use enforce precision since it returns a number and we are
      // doing a RegExp over the string
      const fixed = val.toFixed(nDecimalDigits);
      // separate begin [$1], middle [$2] and decimal digits [$4]
      const parts = new RegExp(`^(-?\\d{1,3})((?:\\d{3})+)(\\.(\\d{${nDecimalDigits}}))?$`).exec(fixed);

      if (parts) {
        // val >= 1000 || val <= -1000
        return (
          parts[1] +
          parts[2].replace(/\d{3}/g, `${thousandsSeparator}$&`) +
          (parts[4] ? decimalSeparator + parts[4] : '')
        );
      }
      return fixed.replace('.', decimalSeparator);
    },

    formatNumber(number) {
      return Number.isFinite(number) ? this.currencyFormat(number, 0, ',', ' ') : number;
    },

    formatToNok(belop) {
      return Number.isFinite(belop) ? `kr ${this.currencyFormat(belop, 0, ',', ' ')}` : belop;
    },

    displayName(taksttypetaksttype) {
      const taksttypePredefined = this.taksttyper.find((type) => type.key === taksttypetaksttype);
      return taksttypePredefined ? taksttypePredefined.displayName : taksttypetaksttype;
    },

    takstTypeDisplayNavn(skatteverditype) {
      if (skatteverditype === 'V') {
        return `${this.displayName(skatteverditype)}*`;
      }
      return this.displayName(skatteverditype);
    },

    harTaksttypeMedPropertyNotNull(utskrivningsvedtak, propertyName) {
      return utskrivningsvedtak.skatteverdityper.some((taksttype) => taksttype[propertyName] !== undefined);
    },

    harTaksttypeVerdiIkkeNull(utskrivningsvedtak, propertyName) {
      return utskrivningsvedtak.skatteverdityper.some((taksttype) => taksttype[propertyName] !== 0.0);
    },

    skalSkatteverditypeVises(utskrivningsvedtak, skatteverdityperSomIkkeSkalVises) {
      return utskrivningsvedtak.skatteverdityper.some(({ skatteverditype }) =>
        skatteverdityperSomIkkeSkalVises.indexOf(skatteverditype)
      );
    },

    harTaksttypeBolig(utskrivningsvedtak) {
      return utskrivningsvedtak.skatteverdityper.some(({ skatteverditype }) => skatteverditype === 'B');
    },

    harTaksttypeFormuesgrunnlag(utskrivningsvedtak) {
      return utskrivningsvedtak.skatteverdityper.some(({ skatteverditype }) => skatteverditype === 'F');
    },

    harTaksttypeFormuesgrunnlagEllerSerskiltgrunnlag(utskrivningsvedtak) {
      return utskrivningsvedtak.skatteverdityper.some(({ skatteverditype }) => skatteverditype === ('F' || 'V'));
    },

    harKunTaksttypeFormuesgrunnlagEllerSerskiltgrunnlag(utskrivningsvedtak) {
      const typer = [];
      const verdier = ['F', 'V'];
      const verdier2 = ['F'];
      const verdier3 = ['V'];
      // eslint-disable-next-line array-callback-return
      utskrivningsvedtak.skatteverdityper.some(({ skatteverditype }) => {
        typer.push(skatteverditype);
      });
      return (
        typer.sort().toString() === verdier.sort().toString() ||
        typer.sort().toString() === verdier2.sort().toString() ||
        typer.sort().toString() === verdier3.sort().toString()
      );
    },

    harTaksttypeSaerskiltGrunnlag(utskrivningsvedtak) {
      return utskrivningsvedtak.skatteverdityper.some(({ skatteverditype }) => skatteverditype === 'V');
    },

    toNumber(val) {
      // numberic values should come first because of -0
      if (typeof val === 'number') return val;
      // we want all falsy values (besides -0) to return zero to avoid
      // headaches
      if (!val) return 0;
      if (typeof val === 'string') return parseFloat(val);
      // arrays are edge cases. `Number([4]) === 4`
      if (val.isArray) return NaN;
      return Number(val);
    },

    changePage() {
      if (!this.totalCount) return;
      const startIndex = (this.current - 1) * this.pageSize;
      this.getSkatteeiendommer(
        '1d138825-68d7-4606-ae6c-95125c7b2874',
        this.currentRepresentation.identifikator,
        startIndex
      );
    },

    setPage(pageNo) {
      this.current = pageNo;
      this.changePage();
    },

    isEmpty(value) {
      return (
        value === undefined ||
        value === null ||
        (typeof value === 'object' && Object.keys(value).length === 0) ||
        (typeof value === 'string' && value.trim().length === 0)
      );
    },
  },
};
</script>

<style scoped lang="scss">
.text-right {
  min-width: 0;
  text-align: right;
}

.eiendomsskatt {
  @media screen and (min-width: 768px) {
    padding-right: 1.25rem;
  }
}

.flex {
  display: flex;

  &--grow {
    flex-grow: 1;
    justify-content: space-between;
    margin-left: 1rem;
  }
}

.pull-right {
  flex-grow: 1;
  justify-content: space-between;
  float: right;
  text-align: right;
}

.title-description {
  display: block;

  @media screen and (min-width: 768px) {
    display: inline;
  }
}
</style>
